// Tenantra Inc. - Professional Whitepaper (Balanced Design)
import { useState } from 'react';
import { ArrowLeft, Building2, Download, Printer, Menu, X, ExternalLink } from 'lucide-react';
import { Logo, LogoMark } from '@/components/Logo';
import { Button } from '@/components/ui/button';

const WHITEPAPER_VERSION = "1.0";
const RELEASE_DATE = "December 2025";

// Tokenomics Data
const TOKENOMICS = [
  { label: 'Community & Rewards', percent: 40, amount: '400M', color: '#14B8A6' },
  { label: 'Ecosystem Development', percent: 20, amount: '200M', color: '#6366F1' },
  { label: 'Team & Advisors', percent: 15, amount: '150M', color: '#A855F7' },
  { label: 'Liquidity', percent: 15, amount: '150M', color: '#F59E0B' },
  { label: 'Treasury', percent: 10, amount: '100M', color: '#64748B' },
];

// Pie Chart Component
function TokenPieChart() {
  let cumulativePercent = 0;
  
  const getCoordinatesForPercent = (percent) => {
    const x = Math.cos(2 * Math.PI * percent);
    const y = Math.sin(2 * Math.PI * percent);
    return [x, y];
  };

  return (
    <div className="relative w-40 h-40 sm:w-48 sm:h-48 mx-auto">
      <svg viewBox="-1 -1 2 2" className="transform -rotate-90 w-full h-full drop-shadow-lg">
        {TOKENOMICS.map((item, i) => {
          const [startX, startY] = getCoordinatesForPercent(cumulativePercent / 100);
          cumulativePercent += item.percent;
          const [endX, endY] = getCoordinatesForPercent(cumulativePercent / 100);
          const largeArcFlag = item.percent > 50 ? 1 : 0;
          const pathData = [
            `M ${startX} ${startY}`,
            `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`,
            `L 0 0`,
          ].join(' ');
          
          return (
            <path
              key={i}
              d={pathData}
              fill={item.color}
              className="hover:opacity-80 transition-opacity cursor-pointer"
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="0.02"
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-slate-900 flex items-center justify-center shadow-inner">
          <div className="text-center">
            <p className="text-lg sm:text-xl font-bold text-white">1B</p>
            <p className="text-[9px] sm:text-[10px] text-slate-400">SUPPLY</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Navigation Items
const NAV_ITEMS = [
  { id: 'overview', label: '1. Overview' },
  { id: 'problem', label: '2. The Problem' },
  { id: 'solution', label: '3. Our Solution' },
  { id: 'platform', label: '4. Platform' },
  { id: 'ecosystem', label: '5. Web3 Ecosystem' },
  { id: 'tokenomics', label: '6. Tokenomics' },
  { id: 'roadmap', label: '7. Roadmap' },
  { id: 'team', label: '8. Team' },
  { id: 'disclaimer', label: '9. Disclaimer' },
];

export default function WhitepaperPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handlePrint = () => window.print();

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 print:hidden">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              <Logo size="md" />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrint} className="hidden sm:flex text-xs">
                <Printer className="w-4 h-4 mr-1" />
                Print
              </Button>
              <Button size="sm" onClick={handlePrint} className="bg-teal-600 hover:bg-teal-700 text-xs">
                <Download className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">PDF</span>
              </Button>
              <a href="/community" className="text-sm text-gray-500 hover:text-teal-600 ml-2 hidden sm:flex items-center gap-1">
                <ArrowLeft className="w-4 h-4" />
                Back
              </a>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:sticky top-[57px] left-0 h-[calc(100vh-57px)] w-64 bg-gray-50 border-r border-gray-200 
          overflow-y-auto z-40 transition-transform duration-300 print:hidden
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <nav className="p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Contents</p>
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-w-0">
          
          {/* COVER PAGE - Professional Header */}
          <div className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900"></div>
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-96 h-96 bg-teal-500 rounded-full blur-[150px]"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500 rounded-full blur-[100px]"></div>
            </div>
            
            <div className="relative z-10 px-6 sm:px-8 py-12 sm:py-16 text-center">
              <div className="flex items-center justify-center gap-3 mb-6">
                <LogoMark className="w-12 h-12 sm:w-14 sm:h-14" color="#5eead4" />
              </div>
              
              <p className="text-teal-400 text-xs sm:text-sm font-medium tracking-widest mb-3">WHITEPAPER v{WHITEPAPER_VERSION}</p>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
                Tenantra
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-lg mx-auto">
                Building Trust in Canada's Rental Market
              </p>
              
              <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-slate-400">
                <span>{RELEASE_DATE}</span>
                <span className="hidden sm:inline">•</span>
                <span>tenantra.ca</span>
                <span className="hidden sm:inline">•</span>
                <span>support@tenantra.ca</span>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="px-4 sm:px-8 py-8 sm:py-12">

            {/* Section 1: Overview */}
            <section id="overview" className="mb-16 scroll-mt-20">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">1. Executive Overview</h2>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                Tenantra Inc. is building Canada's first rental behaviour bureau, creating a trusted ecosystem where landlords make informed decisions and tenants build verifiable rental reputations.
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                Our platform combines traditional PropTech with Web3 innovation. The Tenantra Platform provides landlords with verified tenant screening reports, while our Web3 Ecosystem, anchored by the Tenantra Aviator game, builds community engagement and distributes ownership through the $TNTRA token.
              </p>

              <p className="text-gray-700 leading-relaxed">
                This whitepaper outlines our vision, technology, tokenomics, and roadmap for building the most trusted rental ecosystem in North America.
              </p>
            </section>

            {/* Section 2: Problem */}
            <section id="problem" className="mb-16 scroll-mt-20">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">2. The Problem</h2>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                Canada's rental market faces a critical trust deficit. With over 4.4 million renter households and a vacancy rate below 2% in major cities, both landlords and tenants struggle with the same fundamental issue: lack of reliable information.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-4">For Landlords</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
                <li>No reliable way to verify tenant rental history</li>
                <li>Credit reports don't capture rental behaviour</li>
                <li>Average eviction costs range from $10,000 to $50,000</li>
                <li>References are easily falsified</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-4">For Tenants</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
                <li>Good rental history has no portable value</li>
                <li>Starting fresh in new cities is difficult</li>
                <li>No way to prove reliability to prospective landlords</li>
                <li>Discrimination occurs without objective data</li>
              </ul>

              <p className="text-gray-700 leading-relaxed">
                The result: over $1.2 billion in annual losses from problematic tenancies in Canada, with both parties suffering from information asymmetry.
              </p>
            </section>

            {/* Section 3: Solution */}
            <section id="solution" className="mb-16 scroll-mt-20">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">3. Our Solution</h2>
              
              <p className="text-gray-700 leading-relaxed mb-4">
                Tenantra creates a verified rental behaviour database, functioning like a credit bureau but specifically for rental history. Our dual-platform approach combines:
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-4">Tenantra Platform (B2B SaaS)</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Our core product for landlords and property managers, providing verified tenant screening, behaviour reports, and dispute resolution. Key features include:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
                <li>Verified landlord accounts with ID and property ownership confirmation</li>
                <li>Tenant behaviour reports covering payment history and lease compliance</li>
                <li>Tenant scores from 0-100 based on verified rental behaviour</li>
                <li>PIPEDA-compliant consent-based data handling</li>
                <li>Fair dispute resolution process for tenants</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-4">Web3 Ecosystem</h3>
              <p className="text-gray-700 leading-relaxed">
                Community building through the Aviator game, $TNTRA token rewards, NFT tier badges, and DAO governance. This creates engaged early supporters who become platform ambassadors.
              </p>
            </section>

            {/* Section 4: Platform */}
            <section id="platform" className="mb-16 scroll-mt-20">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">4. Tenantra Platform</h2>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                The Tenantra Platform is our core B2B SaaS product designed for landlords and property managers to verify tenant rental history and submit behaviour reports.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-4">Key Features</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-8">
                <li><strong>ID Verification:</strong> Verified landlord and property ownership</li>
                <li><strong>Behaviour Reports:</strong> Payment history and lease compliance tracking</li>
                <li><strong>Tenant Scoring:</strong> 0-100 score from verified data</li>
                <li><strong>Analytics:</strong> Screening insights and tools</li>
                <li><strong>Dispute System:</strong> Fair challenge process</li>
                <li><strong>Consent Flow:</strong> PIPEDA compliant handling</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-4">Pricing</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 font-semibold text-gray-900">Plan</th>
                      <th className="text-left py-3 font-semibold text-gray-900">Price</th>
                      <th className="text-left py-3 font-semibold text-gray-900">Best For</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-100">
                      <td className="py-3">Basic</td>
                      <td className="py-3">$29/month</td>
                      <td className="py-3">Individual landlords (1-5 units)</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3">Professional</td>
                      <td className="py-3">$79/month</td>
                      <td className="py-3">Growing portfolios (6-25 units)</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3">Enterprise</td>
                      <td className="py-3">Custom</td>
                      <td className="py-3">Property management companies</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 5: Ecosystem - HIGHLIGHTED */}
            <section id="ecosystem" className="mb-16 scroll-mt-20">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">5. Web3 Ecosystem</h2>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                The Tenantra Aviator Game is more than entertainment. It's our community-building engine that distributes $TNTRA tokens fairly while creating engaged early supporters.
              </p>

              {/* Aviator Game Card - Professional with depth */}
              <div className="relative overflow-hidden rounded-2xl mb-8">
                <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-slate-900 to-slate-800"></div>
                <div className="absolute inset-0 opacity-30">
                  <div className="absolute top-0 right-0 w-48 h-48 bg-purple-500 rounded-full blur-[80px]"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-teal-500 rounded-full blur-[60px]"></div>
                </div>
                
                <div className="relative z-10 p-5 sm:p-8">
                  <h3 className="text-xl sm:text-2xl font-bold text-white mb-1">Tenantra Aviator</h3>
                  <p className="text-purple-300 text-sm mb-5">Telegram Mini App Game</p>
                  
                  <p className="text-slate-300 text-sm sm:text-base mb-6 max-w-lg">
                    A crash-style game where players earn TN Points. The higher you go, the more you earn, but wait too long and you lose it all.
                  </p>
                  
                  <div className="grid grid-cols-3 gap-3 sm:gap-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center border border-white/10">
                      <p className="text-xl sm:text-2xl font-bold text-white">1,000</p>
                      <p className="text-[10px] sm:text-xs text-slate-400 mt-1">Free Points</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center border border-white/10">
                      <p className="text-xl sm:text-2xl font-bold text-white">5</p>
                      <p className="text-[10px] sm:text-xs text-slate-400 mt-1">Reward Tiers</p>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 sm:p-4 text-center border border-white/10">
                      <p className="text-xl sm:text-2xl font-bold text-white">∞</p>
                      <p className="text-[10px] sm:text-xs text-slate-400 mt-1">Potential</p>
                    </div>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tier Rewards System</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Players earn TN Points through gameplay and referrals, unlocking exclusive benefits at each tier:
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 font-semibold text-gray-900">Tier</th>
                      <th className="text-left py-3 font-semibold text-gray-900">Points</th>
                      <th className="text-left py-3 font-semibold text-gray-900">Benefits</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-700">
                    <tr className="border-b border-gray-100">
                      <td className="py-3">Early Supporter</td>
                      <td className="py-3">5,000</td>
                      <td className="py-3">NFT badge, whitelist access</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3">Network Builder</td>
                      <td className="py-3">25,000</td>
                      <td className="py-3">Guaranteed token allocation</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3">Network Insider</td>
                      <td className="py-3">75,000</td>
                      <td className="py-3">Increased allocation, early access</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3">Network Leader</td>
                      <td className="py-3">200,000</td>
                      <td className="py-3">Maximum allocation, governance</td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-3">Genesis Circle</td>
                      <td className="py-3">500,000</td>
                      <td className="py-3">Top-level benefits, strategic partner</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 6: Tokenomics - HIGHLIGHTED */}
            <section id="tokenomics" className="mb-16 scroll-mt-20">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">6. $TNTRA Tokenomics</h2>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                The $TNTRA token is the native utility token powering the Tenantra ecosystem, deployed on The Open Network (TON) blockchain.
              </p>

              {/* Token Overview Card - Professional with pie chart */}
              <div className="relative overflow-hidden rounded-2xl mb-8">
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900"></div>
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500 rounded-full blur-[100px]"></div>
                </div>
                
                <div className="relative z-10 p-5 sm:p-8">
                  {/* Token Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-6 border-b border-white/10">
                    <div>
                      <h3 className="text-2xl sm:text-3xl font-bold text-white">$TNTRA</h3>
                      <p className="text-slate-400 text-sm">Tenantra Token</p>
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="text-2xl sm:text-3xl font-bold text-white">1,000,000,000</p>
                      <p className="text-slate-400 text-sm">Total Supply (Fixed)</p>
                    </div>
                  </div>
                  
                  {/* Pie Chart + Allocation */}
                  <div className="flex flex-col lg:flex-row gap-6 mb-6">
                    <div className="flex-shrink-0">
                      <TokenPieChart />
                    </div>
                    
                    <div className="flex-1">
                      <p className="text-sm font-medium text-white mb-3">Allocation Breakdown</p>
                      <div className="space-y-3">
                        {TOKENOMICS.map((item, i) => (
                          <div key={i} className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }}></div>
                            <span className="text-sm text-slate-300 flex-1">{item.label}</span>
                            <span className="text-sm text-white font-medium">{item.percent}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Contract Info */}
                  <div className="bg-slate-800/50 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-[#0098EA] flex items-center justify-center">
                        <span className="text-white font-bold text-xs">TON</span>
                      </div>
                      <div>
                        <p className="font-medium text-white text-sm">The Open Network</p>
                        <p className="text-slate-400 text-xs">Powered by Telegram</p>
                      </div>
                    </div>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Contract Address</p>
                    <p className="text-xs font-mono text-slate-300 break-all mb-2">
                      EQC7IUDp5S5jZ0LuX5Vd9xc0Wmq5scusjZZeW4MzCkwTZQPi
                    </p>
                    <a 
                      href="https://tonviewer.com/EQC7IUDp5S5jZ0LuX5Vd9xc0Wmq5scusjZZeW4MzCkwTZQPi" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#0098EA] hover:text-[#00b4ff]"
                    >
                      View on TON Explorer
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-4">Token Utility</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li><strong>Platform Payments:</strong> 20% discount on subscriptions when paying with $TNTRA</li>
                <li><strong>Staking Rewards:</strong> Earn passive income by staking tokens</li>
                <li><strong>Governance:</strong> Vote on platform decisions and feature priorities</li>
                <li><strong>Game Rewards:</strong> Convert TN Points to $TNTRA at launch</li>
                <li><strong>Premium Access:</strong> Unlock advanced features and priority support</li>
                <li><strong>NFT Integration:</strong> Mint and trade tier NFTs</li>
              </ul>
            </section>

            {/* Section 7: Roadmap */}
            <section id="roadmap" className="mb-16 scroll-mt-20">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">7. Roadmap</h2>
              
              <div className="space-y-8">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Phase 0: Foundation (Q4 2025) ✓</h3>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1">
                    <li>Aviator Game Launch</li>
                    <li>TN Points System Implementation</li>
                    <li>Community Discord & Telegram Setup</li>
                    <li>Referral Program Activation</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Phase 1: Community Growth (Q1 2026)</h3>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1">
                    <li>Ambassador Program Launch</li>
                    <li>10,000 Active Players Milestone</li>
                    <li>Weekly Tournaments & Competitions</li>
                    <li>Strategic Partnership Announcements</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Phase 2: NFT Collection (Q2 2026)</h3>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1">
                    <li>Tier NFT Minting</li>
                    <li>NFT Staking for Bonus Points</li>
                    <li>Secondary Marketplace Integration</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Phase 3: Token Launch (Q3 2026)</h3>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1">
                    <li>Smart Contract Audit</li>
                    <li>Private Sale for Genesis Circle</li>
                    <li>Public Presale & Community Allocation</li>
                    <li>DEX Listing</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Phase 4: Expansion (Q4 2026)</h3>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1">
                    <li>CEX Listings</li>
                    <li>DAO Governance Launch</li>
                    <li>Staking & Yield Farming</li>
                    <li>Mobile App Release</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Phase 5: Full Integration (2027+)</h3>
                  <ul className="list-disc pl-6 text-gray-700 space-y-1">
                    <li>$TNTRA Payment Integration with Platform</li>
                    <li>Cross-Chain Expansion</li>
                    <li>Global Market Expansion</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 8: Team */}
            <section id="team" className="mb-16 scroll-mt-20">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">8. Team & Vision</h2>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                Tenantra is built by a team of experienced PropTech entrepreneurs, blockchain developers, and real estate professionals based in Toronto, Ontario.
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-4">Our Vision</h3>
              <p className="text-gray-700 leading-relaxed italic border-l-4 border-teal-500 pl-4 mb-8">
                "To become the trusted standard for rental behaviour verification in North America, empowering landlords with reliable data and rewarding tenants for responsible renting, all while building a community-owned ecosystem through Web3 innovation."
              </p>

              <h3 className="text-lg font-semibold text-gray-900 mt-8 mb-4">Company Information</h3>
              <ul className="text-gray-700 space-y-2">
                <li><strong>Founded:</strong> 2025</li>
                <li><strong>Headquarters:</strong> Toronto, Ontario, Canada</li>
                <li><strong>Website:</strong> tenantra.ca</li>
                <li><strong>Contact:</strong> support@tenantra.ca</li>
              </ul>
            </section>

            {/* Section 9: Disclaimer */}
            <section id="disclaimer" className="mb-16 scroll-mt-20">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">9. Legal Disclaimer</h2>
              
              <div className="text-sm text-gray-600 space-y-4">
                <p>
                  <strong>NOT FINANCIAL ADVICE:</strong> This whitepaper is for informational purposes only and does not constitute financial, investment, legal, or tax advice. The information contained herein should not be construed as a recommendation to buy, sell, or hold any cryptocurrency, token, or digital asset.
                </p>
                <p>
                  <strong>FORWARD-LOOKING STATEMENTS:</strong> This document contains forward-looking statements regarding Tenantra's business strategy, product development, and market opportunity. These statements involve known and unknown risks, uncertainties, and other factors that may cause actual results to differ materially.
                </p>
                <p>
                  <strong>NO GUARANTEES:</strong> There is no guarantee that the $TNTRA token will have any value, that the Tenantra platform will achieve its stated objectives, or that any returns will be generated from holding tokens.
                </p>
                <p>
                  <strong>REGULATORY RISKS:</strong> Cryptocurrency and token regulations vary by jurisdiction and are subject to change. It is your responsibility to ensure compliance with applicable laws in your jurisdiction.
                </p>
                <p>
                  <strong>DO YOUR OWN RESEARCH:</strong> Before participating in any token sale or using the Tenantra platform, conduct your own due diligence and consult with qualified professionals.
                </p>
              </div>
            </section>

            {/* Footer */}
            <footer className="text-center pt-8 border-t border-gray-200 text-sm text-gray-500">
              <p className="mb-2">© {new Date().getFullYear()} Tenantra Inc. All rights reserved.</p>
              <p>Whitepaper Version {WHITEPAPER_VERSION} • {RELEASE_DATE}</p>
            </footer>
          </div>
        </main>
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
